// PRE RMA CSB — Les 4 decoupages de tranche d'age (Phase1 3.3).
// ATTENTION: ce sont 4 fonctions INDEPENDANTES, aux bornes differentes.
// Ne jamais les fusionner en une seule "tranche d'age generique".
//
// Toutes les bornes viennent de RULES.tranches (rules.config.ts) — module
// de configuration unique. Ce fichier ne fait qu'appliquer la logique de
// recherche (helper trouverTranche), il ne contient plus aucun chiffre.

import { RULES } from './rules.config.ts';
import type { Genre, TrancheBorne } from './rules.config.ts';

/**
 * Âge pivot sûr : retourne un nombre fini >= 0 (arrondi au jour), ou null si la valeur est absente / non numérique / négative.
 * Accepte aussi une chaîne numérique ("420", valeur renvoyée par certains pilotes SQL ou collée dans un fichier).
 * C'est LA porte d'entrée de tous les calculs : un âge invalide n'est jamais rangé « par défaut » dans la dernière tranche.
 */
export function ageJoursValide(v: unknown): number | null {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
  const n = typeof v === 'number' ? v : Number(String(v).trim().replace(',', '.'));
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n);
}

/** Retourne le label de la première tranche où ageJours <= jusquaJours. */
function trouverTranche(ageJours: number, bornes: readonly TrancheBorne[]): string {
  for (const b of bornes) {
    if (ageJours <= b.jusquaJours) return b.label;
  }
  return bornes[bornes.length - 1].label;
}

/** Colonne O — utilisee par RMA Partie 1 (diagnostics) et ligne "Cas de fièvre". */
export function trancheMorbidite(ageJours: number, genre: Genre): string {
  return `${trouverTranche(ageJours, RULES.tranches.morbidite)} ${genre}`;
}
export const TRANCHES_MORBIDITE = RULES.tranches.morbidite.map((b) => b.label);

/** Colonne P — utilisee par RMA Partie 2 (traumatismes/violences) et Partie 6 (CSAJ). */
export function trancheTraumatisme(ageJours: number, genre: Genre): string {
  return `${trouverTranche(ageJours, RULES.tranches.traumatisme)} ${genre}`;
}

/**
 * Colonne Q — utilisee par RMA Partie 3 (paludisme) et Tableau 8 du modele officiel.
 * 5 tranches : 0-11 mois | 1-5 ans | 6-13 ans | 14-24 ans | 25 ans et plus.
 * Les bornes sont dans RULES.tranches.palu ; l'ordre est celui des lignes 7 a 11 de T8_Paludisme_PCIME.
 */
export function tranchePalu(ageJours: number): string {
  return trouverTranche(ageJours, RULES.tranches.palu);
}

/** Même découpage que tranchePalu, mais accepte n'importe quelle valeur : null si l'âge est inutilisable (jamais de tranche « par défaut »). */
export function tranchePaluSure(ageJours: unknown): string | null {
  const a = ageJoursValide(ageJours);
  return a === null ? null : tranchePalu(a);
}

/** Colonne R — utilisee par RMA Parties 4 et 5 (nutrition/CRENAS). Vide (null) si >= seuil. */
export function trancheNutrition(ageJours: number): string | null {
  if (ageJours >= RULES.tranches.nutrition.seuilExclusionJours) return null;
  return trouverTranche(ageJours, RULES.tranches.nutrition.bornes);
}

/** Colonne P (Phase1 §3.7, Partie 6 CSAJ). */
export function trancheCsaj(ageJours: number): '10-14' | '15-19' | '20-24' | null {
  for (const b of RULES.tranches.csaj) {
    if (ageJours >= b.minJoursInclus && ageJours < b.maxJoursExclu) return b.label;
  }
  return null;
}
